import { useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { EntitlementInfo, Purchases } from "@revenuecat/purchases-js";
import { useAtomValue, useSetAtom } from "jotai";
import { useNavigate } from "react-router-dom";

import { QUERY_KEYS } from "../constants";
import { TTL } from "../../utils/constants";
import { ROUTES } from "../../utils/constants";
import { RC_ENTITLEMENT_ID } from "../../config/revenuecat";
import { revenueCatReadyAtom } from "../../jotai/atoms/subscription";
import { paywallDrawerOpenAtom } from "../../jotai/atoms/games";
import useAuth from "../../hooks/useAuth";
import Logger from "../../utils/logger";

interface UseSubscriptionReturn {
  isSubscribed: boolean | undefined;
  isLoading: boolean;
  subscriptionData?: EntitlementInfo;
  openPaywall: () => void;
  gateAction: (
    callback: () => void,
    options?: { onBeforePaywall?: () => void }
  ) => void;
}

function useSubscription(): UseSubscriptionReturn {
  const { isLoggedIn } = useAuth();
  const navigate = useNavigate();
  const isRevenueCatReady = useAtomValue(revenueCatReadyAtom);
  const setPaywallOpen = useSetAtom(paywallDrawerOpenAtom);

  const handleGetSubscriptionInfo = async () => {
    try {
      const purchases = Purchases.getSharedInstance();
      const customerInfo = await purchases.getCustomerInfo();
      const subscriptionInfo =
        customerInfo.entitlements.active?.[RC_ENTITLEMENT_ID];
      return subscriptionInfo;
    } catch (error) {
      Logger.error(`[RevenueCat] Failed to fetch customer info: ${error}`);
      return undefined;
    }
  };

  const query = useQuery({
    queryKey: [QUERY_KEYS.SUBSCRIPTION.IS_SUBSCRIBED, isRevenueCatReady],
    queryFn: handleGetSubscriptionInfo,
    enabled: isLoggedIn && isRevenueCatReady,
    staleTime: TTL.MINUTES(5),
    gcTime: TTL.HOURS(1),
  });

  const isSubscribed = query.isLoading ? undefined : query.data !== undefined;

  const openPaywall = useCallback(() => {
    setPaywallOpen(true);
  }, [setPaywallOpen]);

  const gateAction = useCallback(
    (callback: () => void, options?: { onBeforePaywall?: () => void }) => {
      if (!isLoggedIn) {
        options?.onBeforePaywall?.();
        navigate(ROUTES.ROOT.LOG_IN);
        return;
      }
      if (query.isLoading) return;
      if (!isSubscribed) {
        options?.onBeforePaywall?.();
        setPaywallOpen(true);
        return;
      }
      callback();
    },
    [isLoggedIn, query.isLoading, isSubscribed, setPaywallOpen, navigate]
  );

  return {
    isSubscribed,
    isLoading: query.isLoading,
    subscriptionData: query.data,
    openPaywall,
    gateAction,
  };
}

export default useSubscription;
